// src/pages/UnitForm.js
import { faPlus, faTrashAlt, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { getAdditionalTypesByStageId } from '../services/AdditionalTypeServices';
import { getBlockById } from '../services/BlockServices';
import { getStageById } from '../services/StageServices';
import { getTypologiesByStageId } from '../services/TypologyServices'; // Para obtener la lista de tipologías
import { getUnitById, createUnit, updateUnit } from '../services/UnitServices'; // Asumimos que tienes estos servicios
import { updateComplementaryUnit } from '../services/ComplementaryUnitService';

import './Forms.css';
import styles from './Table.module.css';

function UnitForm() {
  const { id, blockId } = useParams();
  const [unit, setUnit] = useState({
    createdAt: "",
    code: '',
    name: '',
    slug: '',
    buildArea: '',
    privateArea: '',
    terraceArea: '',
    terraceNumber: '',
    balconyNumber: '',
    terracePrivateArea: '',
    balconyArea: '',
    mezzanineArea: '',
    bedrooms: '',
    bathrooms: '',
    parkingSpaces: '',
    floor: '',
    status: 'available',
    typologyId: '',
    priceType: 'listPrice',
    fixedPrice: '',
    priceList: '',
    glbCode: '',
    complementaryUnits: []
  });
  const [additionalTypes, setAdditionalTypes] = useState([]);
  const [blockSlug, setBlockSlug] = useState(''); // Guardamos el slug del bloque
  const [error, setError] = useState(null);
  const [priceLists, setPriceLists] = useState([]);
  const [stageId, setStageId] = useState('');
  const [typologies, setTypologies] = useState([]); // Lista de tipologías
  const [loading, setLoading] = useState(true);
  const [availableComplementaryUnits, setAvailableComplementaryUnits] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
  const [selectedComplementary, setSelectedComplementary] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const block = await getBlockById(blockId);
        setBlockSlug(block.slug);
        setStageId(block.stageId);

        const stage = await getStageById(block.stageId);
        setPriceLists(stage.priceLists);

        const [typologyList, additionalTypes] = await Promise.all([
          getTypologiesByStageId(block.stageId),
          getAdditionalTypesByStageId(block.stageId)
        ]);
        setTypologies(typologyList);
        setAdditionalTypes(additionalTypes || []);

        const stageData = await getStageById(block.stageId);
        const allComplementaryUnits = stageData.complementaryUnits || [];
        setAvailableComplementaryUnits(allComplementaryUnits);

        if (id) {
          const unitData = await getUnitById(id);
          if (unitData) {
            const unitComplementary = allComplementaryUnits.filter(
              cu => cu.unitId === id
            );

            setUnit({
              ...unitData.unit,
              priceType: unitData.unit.priceType || 'listPrice',
              complementaryUnits: unitComplementary.length > 0
                ? unitComplementary
                : unitData.unit.complementaryUnits || []
            });
          }
        } else {
          const today = new Date().toISOString().split("T")[0];
          setUnit(prev => ({ ...prev, createdAt: today }));
        }
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, blockId]);

  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    const newSlug = `${blockSlug}-${newCode.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-')}`;
    setUnit({ ...unit, code: newCode, slug: newSlug });
  };

  const getAvailableComplementaryUnits = () => {
    return availableComplementaryUnits.filter(u =>
      u.status === 'available' &&          // debe estar disponible
      !u.assigned &&                       // 👈 excluir las ya asignadas
      !unit.complementaryUnits.some(cu => cu.id === u.id) &&
      (!u.unitId || u.unitId === id)       // permitir solo si no tiene unidad o es de esta unidad
    );
  };


  const openDeleteModal = (type, id) => {
    setDeleteInfo({ type, id });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null });
  };

  const confirmDelete = async () => {
    if (deleteInfo.type === 'complementary' && deleteInfo.id) {
      await handleDeleteComplementaryUnit(deleteInfo.id);
    }
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null });
  };


  const assignComplementaryToUnit = async (unitId, complementaryUnitData) => {
    try {
      const requiredFields = ['id', 'stageId', 'slug', 'additionalTypeId'];
      const missingFields = requiredFields.filter(field => !complementaryUnitData[field]);

      if (missingFields.length > 0) {
        throw new Error(`Faltan campos obligatorios: ${missingFields.join(', ')}`);
      }

      const updatedComplementary = {
        ...complementaryUnitData,
        unitId: unitId,
        assigned: true,
        status: 'available'
      };

      await updateComplementaryUnit(updatedComplementary);

      setAvailableComplementaryUnits(prev =>
        prev.filter(cu => cu.id !== complementaryUnitData.id)
      );

      return updatedComplementary;
    } catch (error) {
      console.error("Error al asignar unidad complementaria:", error);
      throw error;
    }
  };

  const handleAddComplementaryUnit = async () => {
    if (!selectedComplementary) return;

    try {
      setLoading(true);
      setError(null);

      const updatedComplementary = await assignComplementaryToUnit(
        id || unit.id,
        selectedComplementary
      );

      setUnit(prev => ({
        ...prev,
        complementaryUnits: [...prev.complementaryUnits, { ...updatedComplementary, assigned: true }]
      }));

      setShowAddModal(false);
      setSelectedComplementary(null);
    } catch (error) {
      setError(`Error al agregar unidad: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteComplementaryUnit = async (complementaryId) => {
    try {
      const complementaryUnit = unit.complementaryUnits.find(cu => cu.id === complementaryId);

      if (!complementaryUnit) {
        throw new Error("Unidad complementaria no encontrada");
      }

      const updateData = {
        ...complementaryUnit,
        unitId: null,
        status: 'available'
      };

      await updateComplementaryUnit(updateData);

      setUnit(prev => ({
        ...prev,
        complementaryUnits: prev.complementaryUnits.filter(cu => cu.id !== complementaryId)
      }));

      setAvailableComplementaryUnits(prev => [...prev, updateData]);

    } catch (error) {
      console.error("Error al desasignar unidad complementaria:", error);
      setError(error.message);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      setLoading(true);
      if (id) {
        await updateUnit(id, unit);
      } else {
        await createUnit({ ...unit, blockId });
      }
      navigate(`/blocks/edit/${stageId}/${blockId}`);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <h2>{id ? 'Editar Unidad' : 'Nueva Unidad'}</h2>
        {error && (
          <div>
            <div className='error-message'>
              Hubo un error: {error}
            </div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>

          <div className="form-group">
            <label htmlFor="unit-createdAt">Fecha de Creación</label>
            <input
              type="date"
              id="unit-createdAt"
              value={unit.createdAt}
              onChange={(e) => setUnit({ ...unit, createdAt: e.target.value })}
              required
              readOnly
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-code">Código de la Unidad</label>
            <input
              type="text"
              id="unit-code"
              value={unit.code}
              onChange={handleCodeChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-name">Nombre</label>
            <input
              type="text"
              id="unit-name"
              value={unit.name}
              onChange={(e) => setUnit({ ...unit, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-buildArea">Área de Construcción</label>
            <input
              type="number"
              id="unit-buildArea"
              value={unit.buildArea}
              onChange={(e) => setUnit({ ...unit, buildArea: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-privateArea">Área Privada</label>
            <input
              type="number"
              id="unit-privateArea"
              value={unit.privateArea}
              onChange={(e) => setUnit({ ...unit, privateArea: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-terraceArea">Área de Terraza</label>
            <input
              type="number"
              id="unit-terraceArea"
              value={unit.terraceArea}
              onChange={(e) => setUnit({ ...unit, terraceArea: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-terracePrivateArea">Área Privada de Terraza</label>
            <input
              type="number"
              id="unit-terracePrivateArea"
              value={unit.terracePrivateArea}
              onChange={(e) => setUnit({ ...unit, terracePrivateArea: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="number-balcony">Numero de balcones</label>
            <input
              type="number"
              id="number-balcony"
              value={unit.balconyNumber}
              onChange={(e) => setUnit({ ...unit, balconyNumber: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="number-terrace">Numero de terrazas</label>
            <input
              type="number"
              id="number-terrace"
              value={unit.terraceNumber}
              onChange={(e) => setUnit({ ...unit, terraceNumber: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-balconyArea">Área de Balcón</label>
            <input
              type="number"
              id="unit-balconyArea"
              value={unit.balconyArea}
              onChange={(e) => setUnit({ ...unit, balconyArea: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-mezzanineArea">Área de Mezzanine</label>
            <input
              type="number"
              id="unit-mezzanineArea"
              value={unit.mezzanineArea}
              onChange={(e) => setUnit({ ...unit, mezzanineArea: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-bedrooms">Dormitorios</label>
            <input
              type="number"
              id="unit-bedrooms"
              value={unit.bedrooms}
              onChange={(e) => setUnit({ ...unit, bedrooms: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-bathrooms">Baños</label>
            <input
              type="number"
              id="unit-bathrooms"
              value={unit.bathrooms}
              onChange={(e) => setUnit({ ...unit, bathrooms: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-floor">Piso</label>
            <input
              type="number"
              id="unit-floor"
              value={unit.floor}
              onChange={(e) => setUnit({ ...unit, floor: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-status">Estado</label>
            <select
              value={unit.status}
              id="unit-status"
              onChange={(e) => setUnit({ ...unit, status: e.target.value })}
              required
            >
              <option value="available">Disponible</option>
              <option value="sold">Vendido</option>
              <option value="optioned">Opcionado</option>
              <option value="reserved">Reservado</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="unit-typologyId">Tipología</label>
            <select
              value={unit.typologyId}
              id="unit-typologyId"
              onChange={(e) => setUnit({ ...unit, typologyId: e.target.value })}
              required
            >
              <option value="">Seleccione una tipología</option>
              {typologies.map((typology) => (
                <option key={typology.id} value={typology.id}>
                  {typology.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Tipo de Precio</label>
            <div>
              <label>
                <input
                  type="radio"
                  name="priceType"
                  value="listPrice"
                  checked={unit.priceType === 'listPrice'}
                  onChange={(e) => setUnit({ ...unit, priceType: e.target.value })}
                />
                Precio de lista
              </label>
              <label>
                <input
                  type="radio"
                  name="priceType"
                  value="fixedList"
                  checked={unit.priceType === 'fixedList'}
                  onChange={(e) => setUnit({ ...unit, priceType: e.target.value })}
                />
                Lista fija
              </label>
              <label>
                <input
                  type="radio"
                  name="priceType"
                  value="fixedPrice"
                  checked={unit.priceType === 'fixedPrice'}
                  onChange={(e) => setUnit({ ...unit, priceType: e.target.value })}
                />
                Precio fijo
              </label>
            </div>
          </div>

          {unit.priceType === 'fixedPrice' && (
            <>
              <div className="form-group">
                <label htmlFor="unit-fixedPrice">Precio Fijo</label>
                <input
                  type="number"
                  id="unit-fixedPrice"
                  value={unit.fixedPrice || ''}
                  onChange={(e) => setUnit({ ...unit, fixedPrice: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="unit-viewDelta">Aplicar Delta de Vista</label>
                <input
                  type="checkbox"
                  id="unit-viewDelta"
                  checked={unit.viewDelta || false}
                  onChange={(e) => setUnit({ ...unit, viewDelta: e.target.checked })}
                />
              </div>
            </>
          )}

          {unit.priceType === 'fixedList' && (
            <div className="form-group">
              <label htmlFor="unit-priceList">Lista de precios</label>
              <select
                id="unit-priceList"
                value={unit.priceList}
                onChange={(e) => setUnit({ ...unit, priceList: e.target.value })}
                required
              >
                <option value="">Seleccione una lista de precios</option>
                {priceLists.map((priceList) => (
                  <option key={priceList.id} value={priceList.id}>{priceList.description}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="unit-targetFrame">Frame en rotación 360°</label>
            <input
              type="number"
              id="unit-targetFrame"
              value={unit.targetFrame}
              onChange={(e) => setUnit({ ...unit, targetFrame: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-glb">Código GLB</label>
            <input
              type="text"
              id="unit-glb"
              value={unit.glbCode}
              onChange={(e) => setUnit({ ...unit, glbCode: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="unit-slug">Slug (auto-generado)</label>
            <input
              type="text"
              id="unit-slug"
              value={unit.slug}
              readOnly
            />
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">{id ? 'Guardar Cambios' : 'Agregar Unidad'}</button>
          </div>
        </form>
        <div className="form-footer">
          <Link to={`/blocks/edit/${stageId}/${blockId}`} className="back-link">Regresar a Bloque</Link>
        </div>
      </div>


      <div className="stages-container">
        <div className="sections-container">
          <h2>Unidades Complementarias Asignadas</h2>

          <button onClick={() => setShowAddModal(true)} className="btn-add">
            <FontAwesomeIcon icon={faPlus} /> Asociar Unidad Complementaria
          </button>

          {/* Modal para agregar unidades */}
          {showAddModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h2>Asociar Unidad Complementaria</h2>
                <div className="form-group">
                  <label>Seleccionar Unidad:</label>
                  <select
                    value={selectedComplementary?.id || ''}
                    onChange={(e) => {
                      const unitId = e.target.value;
                      const unit = getAvailableComplementaryUnits().find(u => u.id === unitId);
                      setSelectedComplementary(unit);
                    }}
                    disabled={loading}
                  >
                    <option value="">Seleccione una unidad</option>
                    {getAvailableComplementaryUnits().map(cu => {
                      const typeInfo = additionalTypes.find(t => t.id === cu.additionalTypeId);
                      return (
                        <option key={cu.id} value={cu.id}>
                          {cu.code} - {typeInfo?.name || 'Sin tipo'}
                          ({cu.status === 'available' ? 'Disponible' : 'Asignada'})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {selectedComplementary && (
                  <div className="unit-details">
                    <p><strong>Área:</strong> {selectedComplementary.area || 'N/A'} m²</p>
                    <p><strong>Estado:</strong> {selectedComplementary.status}</p>
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    className='btn-submit'
                    onClick={handleAddComplementaryUnit}
                    disabled={!selectedComplementary || loading}>
                    {loading ? 'Procesando...' : 'Asociar'}
                  </button>
                  <button
                    className='btn-cancel'
                    onClick={() => {
                      setShowAddModal(false);
                      setSelectedComplementary(null);
                    }}
                    disabled={loading}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th>Asignada</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {unit.complementaryUnits
                ?.filter(cu => cu.unitId === (id || unit.id))  // Filtra por unitId
                .map((cu) => {
                  const typeInfo = additionalTypes.find(t => t.id === cu.additionalTypeId);
                  return (
                    <tr key={cu.id}>
                      <td>{cu.code}</td>
                      <td>
                        {typeInfo?.name || 'Sin tipo'}
                        {typeInfo?.group && ` (${typeInfo.group === 'PARK' ? 'Parqueadero' : 'Cuarto útil'})`}
                      </td>
                      <td>{cu.assigned ? 'Sí' : 'No'}</td>  {/* 👈 Sí / No */}
                      <td>
                        {cu.status === 'available' && 'Disponible'}
                        {cu.status === 'sold' && 'Vendido'}
                      </td>
                      <td>
                        <div className="actions-container">
                          <button
                            className="btn-edit"
                            onClick={() => navigate(`/complementary-units/edit/${stageId}/${cu.id}`)}>
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => openDeleteModal('complementary', cu.id)}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {unit.complementaryUnits?.filter(cu => cu.unitId === (id || unit.id)).length === 0 && (
            <div style={{ color: 'black', marginTop: '10px' }}>
              No hay unidades complementarias asignadas a esta unidad
            </div>
          )}

          {showDeleteModal && (
            <div className="media-modal-overlay" onClick={closeDeleteModal}>
              <div className="media-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-content-h3">¿Seguro que deseas eliminar este archivo?</h3>
                <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                  <button className="btn-submit" onClick={confirmDelete}>Aceptar</button>
                  <button className="btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UnitForm;
